import {
	IReactionDisposer,
	action,
	observable,
	reaction,
	runInAction,
} from "mobx";
import {
	MappedEvent,
	ResultsFormattedData,
} from "@data-types/results/ResultsContentData";
import { ResultsMenuStore } from "@state/stores/results";
import ResultsService from "@services/results/ResultsService";

export default class ResultsDataStore {
	menuStore: ResultsMenuStore;
	bettingTypeIds: string[] | null = null;
	resultsDataReaction: IReactionDisposer;

	@observable resultData:
		| ResultsFormattedData<Map<string, MappedEvent[]>>[]
		| null = null;
	@observable error: Error | null = null;
	@observable dayParam: number = 0;
	@observable hasPendingRequest = false;
	@observable searchPhrase: string | null = null;

	constructor(menuStore: ResultsMenuStore) {
		this.menuStore = menuStore;
		this.resultsDataReaction = reaction(
			() => [
				this.menuStore.selectedTournaments,
				this.dayParam,
				this.searchPhrase,
			],
			() => this.onInitialize(),
			{ fireImmediately: true }
		);
	}

	@action.bound
	async onInitialize(): Promise<void> {
		this.hasPendingRequest = true;
		this.error = null;

		try {
			const bettingTypeIds = await this.getBettingTypeIds();
			const selectedTournaments = this.menuStore.selectedTournaments;

			const requestFilter = {
				startDayFromToday: this.dayParam,
				endDayFromToday: this.dayParam === -1 ? -1 : null,
				tournamentIds: selectedTournaments.length
					? selectedTournaments.map((t) => t.id)
					: null,
				searchPhrase: this.searchPhrase,
			};

			const resultDataRequest = await ResultsService.getResults(
				requestFilter,
				bettingTypeIds
			);

			if (resultDataRequest.success) {
				runInAction(() => {
					this.resultData = resultDataRequest.data;
					this.hasPendingRequest = false;
				});
			}
		} catch (error) {
			runInAction(() => {
				this.resultData = [];
				this.hasPendingRequest = false;
				this.error = error;
			});
		}
	}

	@action.bound
	selectDay(dayParamValue: number) {
		if (this.hasPendingRequest) {
			return;
		}

		if (this.dayParam !== dayParamValue) {
			this.dayParam = dayParamValue;
		}
	}

	@action.bound
	updateSearchPhrase() {
		this.searchPhrase =
			this.menuStore.searchBarStore.searchTerm.trim().toLowerCase() ||
			null;

		if (this.searchPhrase) {
			this.menuStore.resetSelectedTournaments();
		}

		this.menuStore.updateDayParam();
	}

	@action.bound
	clearSearchPhrase() {
		this.menuStore.searchBarStore.onTermChange("");
		this.updateSearchPhrase();
	}

	@action
	async getBettingTypeIds() {
		if (this.bettingTypeIds) {
			return this.bettingTypeIds;
		}

		const bettingTypeIds = await ResultsService.getBettingTypeIds();

		runInAction(() => {
			this.bettingTypeIds = bettingTypeIds;
		});

		return bettingTypeIds;
	}

	@action.bound
	onDispose() {
		this.resultsDataReaction();
	}
}
